<template>
	<div class="activities-shell container py-3 pb-5">
		<JuniperPageHeader back="/" title="Activities" subtitle="Audit log of changes across MC2" />

		<!-- Toolbar: action pills + subject select + date range + search -->
		<div class="act-toolbar">
			<div class="act-pills">
				<button
					v-for="opt in actionOptions"
					:key="opt.value"
					type="button"
					class="act-pill"
					:class="{ active: filterAction === opt.value }"
					@click="setActionFilter(opt.value)"
				>
					{{ opt.label }}
				</button>
			</div>

			<div class="act-toolbar__right">
				<select v-model="filterSubject" class="act-select" @change="applyFilters">
					<option value="">All subjects</option>
					<option v-for="st in subjectTypes" :key="st" :value="st">{{ shortSubject(st) }}</option>
				</select>
				<input type="date" class="act-date" v-model="dateFrom" title="From" @change="applyFilters" />
				<span class="act-date-sep">–</span>
				<input type="date" class="act-date" v-model="dateTo" title="To" @change="applyFilters" />
				<div class="act-search">
					<i class="fas fa-search act-search__icon"></i>
					<input
						type="text"
						class="act-search__input"
						placeholder="Search user (Enter)"
						v-model.trim="search"
						@keyup.enter="applyFilters"
					/>
					<button v-if="search" type="button" class="act-search__clear" aria-label="Clear" @click="clearSearch">
						<i class="fas fa-times"></i>
					</button>
				</div>
				<button v-if="hasActiveFilters" class="act-icon-btn act-icon-btn--danger" title="Clear all filters" @click="clearFilters">
					<i class="fas fa-eraser"></i>
				</button>
			</div>
		</div>

		<!-- Table -->
		<div class="ada-card">
			<div v-if="loading && tableData.length === 0" class="act-empty">
				<i class="fas fa-spinner fa-spin me-2"></i>Loading…
			</div>
			<div v-else-if="!loading && tableData.length === 0" class="act-empty">No activities found.</div>

			<template v-else>
				<div class="act-table-wrap">
					<div v-if="loading" class="act-loading-overlay">
						<span class="spinner-border spinner-border-sm me-2" role="status"></span>
						Loading…
					</div>
					<div :class="{ 'act-table-blurred': loading }">
				<table class="act-table">
					<thead>
						<tr>
							<th class="act-col-id">ID</th>
							<th>Action</th>
							<th>User</th>
							<th>Subject</th>
							<th>Date</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="a in tableData" :key="a.id" class="act-row" @click="openDetails(a)">
							<td class="act-col-id act-muted">#{{ a.id }}</td>
							<td>
								<span class="act-badge" :class="actionClass(a.action)">{{ a.action }}</span>
							</td>
							<td>
								<template v-if="a.user">
									<span class="act-user">{{ a.user.name }}</span>
									<span class="act-muted act-email">{{ a.user.email }}</span>
								</template>
								<span v-else class="act-muted">System</span>
							</td>
							<td>
								{{ shortSubject(a.subject_type) }}
								<span class="act-subject-id">#{{ a.subject_id }}</span>
							</td>
							<td class="act-muted" :title="a.datetime">{{ fromNowUtc(a.created_at) }}</td>
						</tr>
					</tbody>
				</table>
					</div>
				</div>

				<div class="act-pagination">
					<span class="act-muted act-pagination__info">{{ rangeLabel }}</span>
					<div class="act-pagination__nav">
						<button
							type="button"
							class="act-page-link act-page-link--arrow"
							:disabled="loading || page <= 1"
							title="Previous page"
							@click="goToPage(page - 1)"
						>
							<i class="fas fa-chevron-left"></i>
						</button>
						<template v-for="(p, i) in pages">
							<span v-if="p === '...'" :key="'dots-' + i" class="act-page-dots">…</span>
							<button
								v-else
								:key="'p-' + p"
								type="button"
								class="act-page-link"
								:class="{ active: p === page }"
								:disabled="loading"
								@click="goToPage(p)"
							>
								{{ p }}
							</button>
						</template>
						<button
							type="button"
							class="act-page-link act-page-link--arrow"
							:disabled="loading || !hasMore"
							title="Next page"
							@click="goToPage(page + 1)"
						>
							<i class="fas fa-chevron-right"></i>
						</button>
					</div>
				</div>
			</template>
		</div>

		<!-- Details modal — Vue-controlled backdrop (no Bootstrap JS) -->
		<div v-if="detailsOpen" class="act-modal-backdrop" @click.self="closeDetails">
			<div class="act-modal">
				<div class="act-modal__head">
					<div>
						<span class="act-badge" :class="actionClass(selected.action)">{{ selected.action }}</span>
						<strong class="ms-2">{{ shortSubject(selected.subject_type) }}</strong>
						<a v-if="subjectLink(selected)" :href="subjectLink(selected)" target="_blank" rel="noopener" class="ms-1">
							#{{ selected.subject_id }}
						</a>
						<span v-else class="ms-1">#{{ selected.subject_id }}</span>
						<div class="act-modal__sub act-muted">
							#{{ selected.id }} · {{ selected.datetime }} · {{ selected.user ? selected.user.name : 'System' }}
						</div>
					</div>
					<button type="button" class="act-modal__close" aria-label="Close" @click="closeDetails">
						<i class="fas fa-times"></i>
					</button>
				</div>

				<div class="act-modal__body">
					<div class="act-diff">
						<div v-if="parsedOld(selected)" class="act-diff__col">
							<div class="act-diff__title">Old value</div>
							<div v-for="(val, key) in parsedOld(selected)" :key="'o-' + key" class="act-diff__row">
								<span class="act-diff__key">{{ key }}</span>
								<span class="act-diff__val">{{ formatValue(val) }}</span>
							</div>
						</div>
						<div v-if="parsedNew(selected)" class="act-diff__col">
							<div class="act-diff__title">New value</div>
							<div v-for="(val, key) in parsedNew(selected)" :key="'n-' + key" class="act-diff__row">
								<span class="act-diff__key">{{ key }}</span>
								<span class="act-diff__val">{{ formatValue(val) }}</span>
							</div>
						</div>
						<div v-if="!parsedOld(selected) && !parsedNew(selected)" class="act-muted">
							No value data recorded for this activity.
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import JuniperPageHeader from '@/components/juniper/layout/JuniperPageHeader.vue'
import { mapState } from 'vuex'
import restApi from '@/api'
import { fromNowUtc } from '@/utils/time'

const SUBJECT_ROUTES = {
	Client: 'clients',
	ClientContact: 'client-contacts',
	Municipality: 'municipalities',
	Contact: 'contacts',
	Carnivore: 'carnivores',
	Campaign: 'campaigns',
}

export default {
	name: 'Activities',
	components: { JuniperPageHeader },
	data() {
		return {
			tableData: [],
			loading: false,
			page: 1,
			pagination: {},
			subjectTypes: [],
			filterAction: '',
			filterSubject: '',
			dateFrom: '',
			dateTo: '',
			search: '',
			selected: null,
			detailsOpen: false,
			actionOptions: [
				{ value: '', label: 'All' },
				{ value: 'CREATED', label: 'Created' },
				{ value: 'UPDATED', label: 'Updated' },
				{ value: 'DELETED', label: 'Deleted' },
			],
		}
	},
	computed: {
		...mapState(['authUser']),
		hasActiveFilters() {
			return !!(this.filterAction || this.filterSubject || this.dateFrom || this.dateTo || this.search)
		},
		hasMore() {
			return this.pagination.current_page && this.pagination.last_page
				? this.pagination.current_page < this.pagination.last_page
				: false
		},
		perPage() {
			return 20
		},
		rangeLabel() {
			const total = this.pagination.total || 0
			if (!total) return '0 of 0'
			const from = (this.page - 1) * this.perPage + 1
			const to = Math.min(this.page * this.perPage, total)
			return `${from}–${to} of ${total}`
		},
		// Windowed page list with ellipsis: 1 … 44 45 46 47 48 … 2000
		pages() {
			const last = this.pagination.last_page || 1
			const cur = this.page
			const delta = 2
			const out = []
			let prev = 0
			for (let i = 1; i <= last; i++) {
				if (i === 1 || i === last || (i >= cur - delta && i <= cur + delta)) {
					if (prev && i - prev > 1) out.push('...')
					out.push(i)
					prev = i
				}
			}
			return out
		},
	},
	created() {
		this.getActivities(true)
		this.loadSubjectTypes()
	},
	methods: {
		fromNowUtc,
		buildParams() {
			const params = { page: this.page, perPage: this.perPage, orderBy: 'created_at', direction: 'desc' }
			if (this.filterAction) params.action = this.filterAction
			if (this.filterSubject) params.subject_type = this.filterSubject
			if (this.search) params.search = this.search
			if (this.dateTo) {
				params.tocreated_at = this.dateTo
				if (this.dateFrom) params.fromcreated_at = this.dateFrom
			}
			return params
		},
		getActivities(reset = false) {
			this.loading = true
			if (reset) this.page = 1
			restApi
				.get('/activities', { params: this.buildParams() })
				.then(({ data }) => {
					this.tableData = data.data || []
					this.pagination = { current_page: data.current_page, last_page: data.last_page, total: data.total }
					this.loading = false
				})
				.catch(() => {
					this.loading = false
				})
		},
		goToPage(page) {
			if (this.loading || page < 1 || (this.pagination.last_page && page > this.pagination.last_page)) return
			this.page = page
			this.getActivities(false)
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
		loadSubjectTypes() {
			restApi
				.get('/activities/subject-types')
				.then(({ data }) => {
					this.subjectTypes = Array.isArray(data) ? data : []
				})
				.catch(() => {
					this.subjectTypes = []
				})
		},
		setActionFilter(value) {
			this.filterAction = value
			this.applyFilters()
		},
		applyFilters() {
			this.getActivities(true)
		},
		clearSearch() {
			this.search = ''
			this.applyFilters()
		},
		clearFilters() {
			this.filterAction = ''
			this.filterSubject = ''
			this.dateFrom = ''
			this.dateTo = ''
			this.search = ''
			this.applyFilters()
		},
		openDetails(activity) {
			this.selected = activity
			this.detailsOpen = true
		},
		closeDetails() {
			this.detailsOpen = false
		},
		actionClass(action) {
			return {
				'act-badge--created': action === 'CREATED',
				'act-badge--updated': action === 'UPDATED',
				'act-badge--deleted': action === 'DELETED',
			}
		},
		shortSubject(type) {
			if (!type) return '—'
			return type.split('\\').pop()
		},
		subjectLink(activity) {
			const model = this.shortSubject(activity.subject_type)
			const route = SUBJECT_ROUTES[model]
			return route ? `/${route}/edit/${activity.subject_id}` : null
		},
		parseMaybeJson(value) {
			if (value === null || value === undefined || value === '') return null
			if (typeof value === 'object') return value
			try {
				const parsed = JSON.parse(value)
				return parsed && typeof parsed === 'object' ? parsed : { value: parsed }
			} catch (e) {
				return { value }
			}
		},
		parsedOld(activity) {
			return this.parseMaybeJson(activity.old_value)
		},
		parsedNew(activity) {
			return this.parseMaybeJson(activity.new_value)
		},
		formatValue(val) {
			if (val === null || val === undefined) return '—'
			if (typeof val === 'object') return JSON.stringify(val)
			return String(val)
		},
	},
}
</script>

<style scoped lang="scss">
$act-border: #e6e8ec;
$act-text: #2b303b;
$act-muted: #8a909c;

.activities-shell {
	max-width: 1100px;
}

/* Toolbar */
.act-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 1rem;
}

.act-pills {
	display: flex;
	gap: 0;
	background: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.act-pill {
	background: none;
	border: none;
	padding: 5px 14px;
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.15s ease;

	&.active {
		background: #fff;
		color: #111827;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	}

	&:hover:not(.active) {
		color: #374151;
	}
}

.act-toolbar__right {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.act-select,
.act-date {
	font-size: 13px;
	padding: 5px 8px;
	border: 1px solid $act-border;
	border-radius: 7px;
	color: #374151;
	background: #fff;
	outline: none;

	&:focus {
		border-color: #2b6cff;
		box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
	}
}

.act-date-sep {
	color: $act-muted;
}

.act-search {
	position: relative;
	width: 220px;

	&__icon {
		position: absolute;
		left: 11px;
		top: 50%;
		transform: translateY(-50%);
		color: $act-muted;
		font-size: 12px;
	}

	&__input {
		width: 100%;
		padding: 5px 28px 5px 30px;
		border: 1px solid $act-border;
		border-radius: 7px;
		font-size: 13px;
		outline: none;

		&:focus {
			border-color: #2b6cff;
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
		}
	}

	&__clear {
		position: absolute;
		right: 8px;
		top: 50%;
		transform: translateY(-50%);
		border: none;
		background: none;
		color: $act-muted;
		cursor: pointer;
		padding: 0;
	}
}

.act-icon-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid $act-border;
	border-radius: 7px;
	background: #fff;
	color: $act-muted;
	cursor: pointer;
	transition: all 0.15s ease;

	&:hover {
		color: $act-text;
		border-color: #c8ccd4;
	}

	&--danger:hover {
		color: #dc3545;
		border-color: #f1c2c7;
	}
}

/* Card + compact flat table (settings language) */
.ada-card {
	border: 1px solid $act-border;
	border-radius: 12px;
	padding: 1rem 1.1rem;
	background: transparent;
}

.act-empty {
	text-align: center;
	padding: 1.5rem 1rem;
	color: $act-muted;
	font-size: 0.9rem;
}

.act-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;

	thead th {
		text-align: left;
		font-weight: 600;
		color: #6c757d;
		padding: 4px 10px 8px 0;
		border-bottom: 1px solid #dee2e6;
	}

	tbody td {
		padding: 7px 10px 7px 0;
		border-bottom: 1px solid #f1f3f5;
		vertical-align: middle;
		color: $act-text;
	}

	tbody tr:last-child td {
		border-bottom: none;
	}
}

.act-row {
	cursor: pointer;

	&:hover td {
		background: #f3f5f9;
	}
}

.act-col-id {
	width: 4rem;
}

.act-muted {
	color: $act-muted;
}

.act-user {
	font-weight: 600;
}

.act-email {
	margin-left: 0.4rem;
	font-size: 12px;
}

.act-subject-id {
	color: $act-muted;
	font-size: 12px;
	margin-left: 0.15rem;
}

.act-badge {
	display: inline-block;
	padding: 1px 9px;
	font-size: 11px;
	font-weight: 700;
	border-radius: 999px;
	text-transform: capitalize;

	&--created {
		background: #d1e7dd;
		color: #0f5132;
	}

	&--updated {
		background: #e7f1ff;
		color: #0b5ed7;
	}

	&--deleted {
		background: #f8d7da;
		color: #842029;
	}
}

/* Pagination — info on the left, prev/next on the right */
.act-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.85rem 0 0.25rem;
	font-size: 0.82rem;
}

.act-pagination__nav {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

/* Plain text page links — same font/size as the range label on the left. */
.act-page-link {
	background: none;
	border: 0;
	padding: 0 4px;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	color: $act-muted;
	cursor: pointer;
	transition: color 0.12s ease;

	&:hover:not(:disabled) {
		color: $act-text;
	}

	&.active {
		color: $act-text;
		font-weight: 700;
	}

	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
}

.act-page-link--arrow {
	font-size: 0.72rem;
}

.act-page-dots {
	color: $act-muted;
	padding: 0 2px;
	user-select: none;
}

/* Loading overlay — table stays visible but blurred underneath */
.act-table-wrap {
	position: relative;
}

.act-table-blurred {
	filter: blur(2px);
	opacity: 0.55;
	pointer-events: none;
	transition: filter 0.15s ease, opacity 0.15s ease;
}

.act-loading-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;
	font-weight: 600;
	color: $act-text;
}

/* Details modal — Vue-controlled */
.act-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 18, 24, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1.5rem;
	z-index: 1080;
}

.act-modal {
	background: #fff;
	border-radius: 14px;
	width: 100%;
	max-width: 760px;
	max-height: 85vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.act-modal__head {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 1rem;
	padding: 1rem 1.15rem 0.75rem;
	border-bottom: 1px solid #f0f1f4;
	font-size: 13px;
}

.act-modal__sub {
	margin-top: 0.25rem;
	font-size: 12px;
}

.act-modal__close {
	border: none;
	background: none;
	color: $act-muted;
	cursor: pointer;
	font-size: 1rem;
	line-height: 1;
	padding: 0;

	&:hover {
		color: $act-text;
	}
}

.act-modal__body {
	padding: 1rem 1.15rem 1.15rem;
	overflow-y: auto;
	font-size: 13px;
}

.act-diff {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
}

.act-diff__col {
	min-width: 0;
}

.act-diff__title {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: $act-muted;
	border-bottom: 1px solid #f0f1f4;
	padding-bottom: 3px;
	margin-bottom: 5px;
}

.act-diff__row {
	display: flex;
	gap: 0.4rem;
	padding: 2px 0;
	word-break: break-word;
}

.act-diff__key {
	color: $act-muted;
	flex-shrink: 0;
}

.act-diff__val {
	color: $act-text;
	word-break: break-all;
}
</style>
